/**
 * Whether an event has been canceled. Canceled events should never link out
 * to registration/event pages — the UI shows them as canceled instead.
 * @param {Object} event - The event object
 * @returns {boolean}
 */
export const isEventCanceled = (event) => event?.eventType === 'Canceled';

/**
 * Normalizes an event's tags into a flat array of label strings, regardless
 * of whether the source data used an array of strings, an array of
 * {name} objects, or a comma-separated string.
 * @param {Array|string} tags - Raw tags value from an event
 * @returns {string[]} Flat array of tag labels
 */
export const normalizeEventTags = (tags) => {
    if (Array.isArray(tags)) {
        return tags.map((tag) => (typeof tag === 'string' ? tag : tag?.name || '')).filter(Boolean);
    }
    if (typeof tags === 'string') {
        return tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    }
    return [];
};

/**
 * Generates a URL-friendly slug for an event
 * @param {Object} event - The event object containing startDate, organization, and title
 * @returns {string} URL-friendly slug
 */
export const generateEventSlug = (event) => {
    const eventDate = parseEventDate(event.startDate) || new Date(0);
    return `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}-${encodeURIComponent(event.organization)}-${encodeURIComponent(event.title)}`.toLowerCase();
};

/**
 * Returns every slug that can identify an event. Merged events retain the
 * slugs of all source listings so old shared links and favorites keep working.
 * @param {Object} event - Event object
 * @returns {string[]}
 */
export const getEventSlugs = (event) => {
    const slugs = Array.isArray(event?.sourceSlugs) ? event.sourceSlugs : [];
    return [...new Set([generateEventSlug(event), ...slugs].filter(Boolean))];
};

export const eventMatchesSlug = (event, slug) => getEventSlugs(event).includes(slug);

export const isEventStarred = (event, starredSlugs) => {
    const starred = new Set(Array.isArray(starredSlugs) ? starredSlugs : []);
    return getEventSlugs(event).some((slug) => starred.has(slug));
};

/**
 * Returns an updated favorites list. Favoriting stores the canonical slug;
 * unfavoriting removes the canonical slug and every preserved source alias.
 */
export const toggleEventStarred = (event, starredSlugs) => {
    const current = Array.isArray(starredSlugs) ? starredSlugs : [];
    const aliases = new Set(getEventSlugs(event));
    if (isEventStarred(event, current)) {
        return current.filter((slug) => !aliases.has(slug));
    }
    return [...current, generateEventSlug(event)];
};

/**
 * Parses common event date formats in a Safari-safe way.
 * @param {Date|string|number} dateInput - Date-like input
 * @returns {Date|null} Parsed Date or null for invalid values
 */
export const parseEventDate = (dateInput) => {
    if (dateInput instanceof Date) {
        return Number.isNaN(dateInput.getTime()) ? null : new Date(dateInput);
    }

    if (typeof dateInput === 'string') {
        const trimmed = dateInput.trim();

        // Reject overflow dates (for example February 30), which JavaScript
        // otherwise silently rolls into the following month.
        const calendarDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/);
        if (calendarDateMatch) {
            const year = Number(calendarDateMatch[1]);
            const month = Number(calendarDateMatch[2]);
            const day = Number(calendarDateMatch[3]);
            const localDate = new Date(year, month - 1, day);
            const calendarDateIsValid = localDate.getFullYear() === year
                && localDate.getMonth() === month - 1
                && localDate.getDate() === day;
            if (!calendarDateIsValid) return null;

            // Date-only values are interpreted as local dates to avoid UTC shifting.
            if (calendarDateMatch[0] === trimmed) return localDate;
        }

        // Safari is stricter with "YYYY-MM-DD HH:mm:ss" than Chromium; normalize to ISO-like local time.
        const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
        const parsed = new Date(normalized);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    const fallback = new Date(dateInput);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/**
 * Creates a local YYYY-MM-DD key from a Date or date-like value.
 * @param {Date|string|number} dateInput - Date-like input
 * @returns {string|null} Local date key or null for invalid dates
 */
export const getLocalDateKey = (dateInput) => {
    const date = parseEventDate(dateInput);
    if (!date) {
        return null;
    }

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Longest multi-day event (in calendar days, inclusive of start and end) that
 * the Month/Week grids repeat on every day it covers. Longer spans (e.g. a
 * 17-day online auction) show only on their start day so they don't crowd
 * out the single-day events that are the grids' primary content.
 */
export const MAX_GRID_SPAN_DAYS = 5;

/**
 * True when an event's start and end fall on different local calendar days.
 * @param {Object} event - Event with startDate/endDate
 * @returns {boolean}
 */
export const isMultiDayEvent = (event) => {
    const startKey = getLocalDateKey(event?.startDate);
    const endKey = getLocalDateKey(event?.endDate);
    return !!startKey && !!endKey && startKey !== endKey;
};

/**
 * Local YYYY-MM-DD keys of the days an event appears on in the Month/Week
 * grids: every day from start through end for multi-day events up to
 * MAX_GRID_SPAN_DAYS long, otherwise just the start day.
 * @param {Object} event - Event with startDate/endDate
 * @returns {string[]} Day keys in chronological order (empty for invalid dates)
 */
export const getEventGridDayKeys = (event) => {
    const startKey = getLocalDateKey(event?.startDate);
    if (!startKey) return [];
    if (!isMultiDayEvent(event)) return [startKey];

    const start = parseEventDate(event.startDate);
    const end = parseEventDate(event.endDate);
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const keys = [];
    while (cursor <= last && keys.length <= MAX_GRID_SPAN_DAYS) {
        keys.push(getLocalDateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return keys.length > MAX_GRID_SPAN_DAYS ? [startKey] : keys;
};

/**
 * Human-readable "when" for an event: its time range ("6:00 PM – 8:30 PM"),
 * "All day" for midnight starts, and a date-range prefix for multi-day
 * events ("Oct 23 – Oct 26 · 8:00 AM – 3:00 PM").
 * @param {Object} event - Event with startDate/endDate
 * @returns {string}
 */
export const formatEventWhen = (event) => {
    const start = parseEventDate(event?.startDate);
    const end = parseEventDate(event?.endDate || event?.startDate);
    if (!start) return 'All day';
    const dateOpts = { month: 'short', day: 'numeric' };
    const span = isMultiDayEvent(event)
        ? `${start.toLocaleDateString([], dateOpts)} – ${end.toLocaleDateString([], dateOpts)}`
        : null;
    if (start.getHours() === 0 && start.getMinutes() === 0) return span || 'All day';
    const opts = { hour: 'numeric', minute: '2-digit' };
    const time = end && end.getTime() !== start.getTime()
        ? `${start.toLocaleTimeString([], opts)} – ${end.toLocaleTimeString([], opts)}`
        : start.toLocaleTimeString([], opts);
    return span ? `${span} · ${time}` : time;
};

/**
 * True when an event appears on the given day in the Month/Week grids
 * (see getEventGridDayKeys).
 * @param {Object} event - Event with startDate/endDate
 * @param {Date|string} day - Day to check
 * @returns {boolean}
 */
export const eventOccursOnDay = (event, day) => {
    const dayKey = getLocalDateKey(day);
    return !!dayKey && getEventGridDayKeys(event).includes(dayKey);
};

/**
 * Normalizes a string for co-host matching: trim, lowercase, collapse
 * internal whitespace. Not exported — only meaningful as a matching key.
 */
const normalizeForMatch = (value) =>
    typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';

/**
 * Returns the list of hosting organizations for an event, each paired with
 * its own event page link. Every event has at least one host; an event
 * merged by mergeCoHostedEvents (same title + start/end time, marketed by
 * more than one organization) carries one entry per original organization.
 * @param {Object} event - The event object
 * @returns {{organization: string, eventURL: string}[]}
 */
export const getEventHosts = (event) => {
    if (Array.isArray(event?.coHosts) && event.coHosts.length > 0) {
        return event.coHosts;
    }
    return [{ organization: event?.organization, eventURL: event?.eventURL }];
};

/**
 * Whether an event is hosted (solely or jointly) by the given organization.
 * Use this instead of `event.organization === org` anywhere events are
 * filtered by organization, so co-hosted events still match each host.
 * @param {Object} event - The event object
 * @param {string} organization - Organization name to check for
 * @returns {boolean}
 */
export const eventHasOrganization = (event, organization) =>
    getEventHosts(event).some((host) => host.organization === organization);

/**
 * Friendly, compact label listing an event's host organization(s) — e.g.
 * "Org A", "Org A & Org B", or "Org A, Org B + 2 more" — for chips and other
 * space-constrained displays that show a single line of organization text.
 * @param {Object} event - The event object
 * @param {number} maxNamed - How many org names to spell out before collapsing the rest into "+N more"
 * @returns {string}
 */
export const formatEventHostsLabel = (event, maxNamed = 2) => {
    const names = getEventHosts(event).map((host) => host.organization).filter(Boolean);
    if (names.length <= 1) return names[0] || '';
    if (names.length <= maxNamed) return names.join(' & ');
    return `${names.slice(0, maxNamed).join(', ')} + ${names.length - maxNamed} more`;
};

/**
 * Merges events that are really one co-hosted event marketed separately by
 * each host — matched on normalized title/location plus identical valid
 * start/end times —
 * into a single event carrying a deduplicated `coHosts` list (one
 * {organization, eventURL} entry per host, in encounter order). All other fields
 * (description, location, tags, etc.) are taken from the first-seen listing.
 *
 * This is a purely client-side presentation merge — it doesn't touch the
 * underlying data schema or API. `event.organization`/`event.eventURL` are
 * left pointing at the first host, so anything that hasn't been updated to
 * read `coHosts` keeps working unchanged.
 * When a categorizer is supplied, every raw listing is categorized before
 * grouping. A merged event then uses the highest-priority source category,
 * avoiding dependence on which organization's listing arrived first.
 * @param {Object[]} events - Raw events for a month (or combined months)
 * @param {{categorize?: Function, categoryPriority?: string[]}} options
 * @returns {Object[]} Events with co-hosted duplicates merged
 */
export const mergeCoHostedEvents = (events, options = {}) => {
    if (!Array.isArray(events) || events.length === 0) {
        return events || [];
    }

    const { categorize, categoryPriority = [] } = options;
    const groups = new Map();
    const order = [];

    events.forEach((sourceEvent, index) => {
        const derivedCategory = categorize ? categorize(sourceEvent) : sourceEvent.derivedCategory;
        const event = derivedCategory ? { ...sourceEvent, derivedCategory } : sourceEvent;
        const startDate = parseEventDate(event.startDate);
        const endDate = parseEventDate(event.endDate || event.startDate);
        const title = normalizeForMatch(event.title);
        // A blank location (common for virtual/Zoom listings) must not block
        // a match — it's folded into a shared placeholder so two listings
        // that both omit it still merge, while two listings that name
        // different actual venues still don't.
        const location = normalizeForMatch(event.location) || 'unspecified-location';

        // Invalid or incomplete identifying data is never safe to merge. The
        // unique suffix deliberately keeps each malformed listing separate.
        const canMerge = Boolean(title && startDate && endDate);
        const key = canMerge
            ? `${title}|${location}|${startDate.getTime()}|${endDate.getTime()}`
            : `unmergeable-${index}`;

        if (!groups.has(key)) {
            groups.set(key, []);
            order.push(key);
        }
        groups.get(key).push(event);
    });

    return order.map((key) => {
        const group = groups.get(key);
        if (group.length === 1) {
            return group[0];
        }

        const coHosts = [...new Map(group.map((event) => {
            const host = { organization: event.organization, eventURL: event.eventURL };
            const hostKey = `${normalizeForMatch(host.organization)}|${normalizeForMatch(host.eventURL)}`;
            return [hostKey, host];
        })).values()];
        const sourceSlugs = [...new Set(group.flatMap((event) => getEventSlugs(event)))];
        const sourceCategories = [...new Set(group.map((event) => event.derivedCategory).filter(Boolean))];
        const derivedCategory = categoryPriority.find((category) => sourceCategories.includes(category))
            || sourceCategories[0];

        return {
            ...group[0],
            ...(derivedCategory ? { derivedCategory } : {}),
            coHosts,
            sourceSlugs,
        };
    });
};

/**
 * Parses a slug to extract date information
 * @param {string} slug - The event slug
 * @returns {Object|null} Object containing year and month, or null if invalid
 */
export const parseEventSlug = (slug) => {
    try {
        const parts = slug.split('-');
        if (parts.length >= 3) {
            const year = parseInt(parts[0]);
            const month = parseInt(parts[1]);
            const day = parseInt(parts[2]);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day) && 
                month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                return { year, month, day };
            }
        }
        return null;
    } catch (error) {
        //console.error('Error parsing event slug:', error);
        return null;
    }
};
